import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { logInfo, permissionDenialReason } from "../../../lib/sentryLogging";
import {
  canEnableTelehealth,
  isSafariBrowser,
  shouldAutoSetupMicrophone,
  shouldClearTelehealthWhenSourcesLost,
  shouldResetTelehealthOnClose,
} from "./recordPageRules";
import type {
  TelehealthAudioManagerLike,
  TelehealthAudioState,
  TelehealthSetupResult,
} from "./types";

export interface UseTelehealthSessionOptions {
  isRecording: boolean;
  isTutorialActive?: boolean;
  currentStepId?: string | null;
  goToStep?: (section: string, stepId: string) => void;
  audioManager: TelehealthAudioManagerLike;
  patientId?: string;
  isMockPatient?: boolean;
}

const EMPTY_AUDIO_STATE: TelehealthAudioState = {
  hasMicrophone: false,
  hasScreenAudio: false,
};

function logTelehealthPermissionDenied(
  result: TelehealthSetupResult,
  source: "microphone" | "telehealth",
  patientId: string | undefined
) {
  if (result.errorName !== "NotAllowedError" && result.errorName !== "PermissionDeniedError") {
    return;
  }
  logInfo("Recording permission denied", "recording.permission_denied", {
    patient_id: patientId,
    source,
    is_telehealth: true,
    error_name: result.errorName,
    denial_reason: permissionDenialReason(result.error),
  });
}

export function useTelehealthSession({
  isRecording,
  isTutorialActive = false,
  currentStepId = null,
  goToStep,
  audioManager,
  patientId,
  isMockPatient = false,
}: UseTelehealthSessionOptions) {
  const [isTelehealth, setIsTelehealth] = useState(false);
  const [showTelehealthModal, setShowTelehealthModal] = useState(false);
  const [audioState, setAudioState] = useState<TelehealthAudioState>(EMPTY_AUDIO_STATE);
  const autoMicSetupAttemptedRef = useRef(false);

  useEffect(() => {
    const unsubscribe = audioManager.subscribe((state) => {
      setAudioState({
        hasMicrophone: state.hasMicrophone,
        hasScreenAudio: state.hasScreenAudio,
      });

      if (
        shouldClearTelehealthWhenSourcesLost({
          isTelehealth,
          hasMicrophone: state.hasMicrophone,
          hasScreenAudio: state.hasScreenAudio,
        })
      ) {
        setIsTelehealth(false);
      }
    });

    return unsubscribe;
  }, [audioManager, isTelehealth]);

  useEffect(() => {
    return () => {
      audioManager.cleanup();
    };
  }, [audioManager]);

  useEffect(() => {
    if (!isTelehealth) {
      autoMicSetupAttemptedRef.current = false;
    }
  }, [isTelehealth]);

  const setupMicrophone = useCallback(async () => {
    const result = await audioManager.setupMicrophone();
    if (!result.success && !result.stale) {
      if (!isMockPatient) {
        logTelehealthPermissionDenied(result, "microphone", patientId);
      }
      toast.error(`Failed to connect microphone: ${result.error}`);
    }
  }, [audioManager, isMockPatient, patientId]);

  useEffect(() => {
    if (
      !shouldAutoSetupMicrophone({
        isTelehealth,
        hasMicrophone: audioState.hasMicrophone,
        alreadyAttempted: autoMicSetupAttemptedRef.current,
      })
    ) {
      return;
    }

    autoMicSetupAttemptedRef.current = true;
    void setupMicrophone();
  }, [audioState.hasMicrophone, isTelehealth, setupMicrophone]);

  const handleTelehealthToggle = useCallback(() => {
    const nextValue = !isTelehealth;
    const decision = canEnableTelehealth({
      isRecording,
      isSafari: nextValue && isSafariBrowser(navigator.userAgent),
    });

    if (!decision.allowed) {
      if (decision.reason === "recording") {
        toast.warning("Cannot change visit type during recording");
      } else if (decision.reason === "safari") {
        toast.error(
          "Please use Chrome, Edge, or Firefox. Safari does not support video call audio capture.",
          { autoClose: 4000 }
        );
      }
      return;
    }

    setIsTelehealth(nextValue);

    if (nextValue) {
      setShowTelehealthModal(true);
    } else {
      audioManager.cleanup();
    }

    if (isTutorialActive && nextValue) {
      goToStep?.("record", "telehealth_microphone_step");
    }
  }, [audioManager, goToStep, isRecording, isTelehealth, isTutorialActive]);

  const closeTelehealthModal = useCallback(() => {
    if (shouldResetTelehealthOnClose(audioState)) {
      setIsTelehealth(false);
      audioManager.cleanup();
    }
    setShowTelehealthModal(false);

    if (isTutorialActive && currentStepId === "telehealth_close_modal") {
      return;
    }
  }, [audioManager, audioState, currentStepId, isTutorialActive]);

  // The video slide is shown on its own, so the setup window closes as the tour
  // reaches it. It closes the same way as anywhere else: if a source never
  // connected, the visit switches back off rather than staying in telehealth
  // mode with no way to reopen setup.
  useEffect(() => {
    if (isTutorialActive && currentStepId === "telehealth_close_modal" && showTelehealthModal) {
      closeTelehealthModal();
    }
  }, [isTutorialActive, currentStepId, showTelehealthModal, closeTelehealthModal]);

  const setupScreenAudio = useCallback(async () => {
    const result = await audioManager.setupScreenAudio();

    if (result.success) {
      toast.info("Patient audio source selected. Select Test Audio to verify audio capture.");
      setShowTelehealthModal(false);
    } else if (!result.stale) {
      if (!isMockPatient) {
        logTelehealthPermissionDenied(result, "telehealth", patientId);
      }
      toast.error(`Failed: ${result.error}`);
    }
  }, [audioManager, isMockPatient, patientId]);

  const handleTelehealthCleanup = useCallback(() => {
    audioManager.cleanup();
    setIsTelehealth(false);
    setAudioState(EMPTY_AUDIO_STATE);
  }, [audioManager]);

  return {
    isTelehealth,
    showTelehealthModal,
    audioState,
    handleTelehealthToggle,
    closeTelehealthModal,
    setupScreenAudio,
    handleTelehealthCleanup,
  };
}

export default useTelehealthSession;
